/* -------------------------------------------------------------------------- */
/* Dependencies                                */
/* -------------------------------------------------------------------------- */
import i18n from '@config/i18n/config';
import { apiClient } from '@lib/api-client';
import {
	queryClient,
	TMutationConfig,
} from '@lib/react-query';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export interface IOrganizationResponse {
	id: string | number;
	organizationName: string;
}

interface ICreateOrganizationPayload {
	name: string;
	location: {
		countryCode: string;
		place: string;
	};
	legalIdentifier: string;
	owner: string;
}

type TUseCreateOrganizationOptions = {
	mutationConfig?: TMutationConfig<
		typeof createOrganization
	>;
};

/* -------------------------------------------------------------------------- */
/* API                                                                        */
/* -------------------------------------------------------------------------- */
export const createOrganization = ({
	body,
}: {
	body: ICreateOrganizationPayload;
}): Promise<IOrganizationResponse> => {
	return apiClient.post('/organizations', body);
};

/* -------------------------------------------------------------------------- */
/* Hook                                                                       */
/* -------------------------------------------------------------------------- */
export const useCreateOrganization = ({
	mutationConfig,
}: TUseCreateOrganizationOptions = {}) => {
	const { onSuccess, onError, ...restConfig } =
		mutationConfig || {};

	return useMutation({
		onSuccess: (...args) => {
			queryClient.invalidateQueries({
				queryKey: ['organizations'],
			});
			queryClient.invalidateQueries({
				queryKey: ['organizationIds'],
			});

			notifications.show({
				title: i18n.t('global.success'),
				message: i18n.t(
					'global.organizationCreatedSuccess'
				),
				color: 'green',
			});
			onSuccess?.(...args);
		},
		onError: (...args) => {
			notifications.show({
				title: i18n.t('global.error'),
				message: i18n.t('global.somethingWentWrong'),
				color: 'var(--mantine-color-igluRed-9)',
			});
			onError?.(...args);
		},
		mutationFn: createOrganization,
		...restConfig,
	});
};
